import React from "react";
import { AlertTriangle, Clock } from "lucide-react";
import { fmtNumber } from "./shared";

function StatCard({ label, value, accent = false, testid }) {
  return (
    <div
      data-testid={testid}
      className="bg-white border border-[#e5e0d8] rounded-sm p-5"
    >
      <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold">
        {label}
      </div>
      <div
        className={`mt-2 font-serif text-3xl ${
          accent ? "text-[#9e2a2b]" : "text-[#1c1a17]"
        }`}
      >
        {value}
      </div>
    </div>
  );
}

export default function PropertyDashboard({ summary }) {
  if (!summary) return null;
  const hasAlert = summary.expired_documents > 0 || summary.pending_renewals > 0;
  return (
    <div data-testid="property-dashboard">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatCard
          testid="stat-lands"
          label="Total Lands"
          value={fmtNumber(summary.total_lands)}
        />
        <StatCard
          testid="stat-land-area"
          label="Total Land (sqft)"
          value={fmtNumber(summary.total_land_area_sqft)}
        />
        <StatCard
          testid="stat-properties"
          label="Total Buildings"
          value={fmtNumber(summary.total_properties)}
        />
        <StatCard
          testid="stat-built-up"
          label="Total Built-up (sqft)"
          value={fmtNumber(summary.total_built_up_area)}
        />
        <StatCard
          testid="stat-allocated"
          label="Allocated Area (sqft)"
          value={fmtNumber(summary.allocated_area)}
        />
        <StatCard
          testid="stat-vacant"
          label="Vacant Area (sqft)"
          value={fmtNumber(summary.vacant_area)}
        />
        <StatCard
          testid="stat-expired"
          label="Expired Documents"
          value={fmtNumber(summary.expired_documents)}
          accent={summary.expired_documents > 0}
        />
        <StatCard
          testid="stat-pending"
          label="Pending Renewals (60d)"
          value={fmtNumber(summary.pending_renewals)}
          accent={summary.pending_renewals > 0}
        />
      </div>

      {hasAlert && (
        <div className="bg-[#9e2a2b]/5 border border-[#9e2a2b]/30 rounded-sm p-4 mb-6 text-sm text-[#9e2a2b] flex items-start gap-3">
          {summary.expired_documents > 0 ? (
            <AlertTriangle
              size={16}
              strokeWidth={1.5}
              className="mt-0.5 shrink-0"
            />
          ) : (
            <Clock size={16} strokeWidth={1.5} className="mt-0.5 shrink-0" />
          )}
          <div>
            {summary.expired_documents > 0 && (
              <div>
                <strong>{summary.expired_documents}</strong> document(s) have expired
                and need renewal.
              </div>
            )}
            {summary.pending_renewals > 0 && (
              <div>
                <strong>{summary.pending_renewals}</strong> document(s) expire in the
                next 60 days.
              </div>
            )}
          </div>
        </div>
      )}

      <div className="bg-white border border-[#e5e0d8] rounded-sm p-6">
        <div className="text-[10px] tracking-[0.2em] uppercase text-[#6b655b] font-semibold mb-4">
          Institute-wise Allocation
        </div>
        {summary.per_institute.length === 0 ? (
          <div className="text-sm text-[#6b655b] italic">
            No institute-use allocations yet.
          </div>
        ) : (
          <ul className="divide-y divide-[#e5e0d8]">
            {summary.per_institute.map((p) => (
              <li
                key={p.institute_id}
                data-testid={`per-inst-${p.institute_id}`}
                className="flex items-center justify-between py-2.5"
              >
                <div>
                  <div className="text-sm text-[#1c1a17]">{p.institute_name}</div>
                  <div className="text-[11px] text-[#6b655b]">
                    {p.count} allocation(s)
                  </div>
                </div>
                <div className="font-mono text-sm text-[#234333]">
                  {fmtNumber(p.allocated_area)} sqft
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
